import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription, combineLatest, distinctUntilChanged, filter, map, switchMap } from 'rxjs';
import { Steps } from 'primeng/steps';
import { MenuItem } from 'primeng/api';

import { MessageService } from '@shared/services/message-service';
import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { WorkedShiftsService } from '@shared/services/worked-shifts.service';
import { getSiteInfo } from '@shared/helpers/site-info';
import { url } from '@shared/helpers/url';
import { SiteConfig } from '@shared/models/site-config';
import { SiteSubinventory, SubinventoryType } from '@shared/models/subinventory';
import { ShiftStatus } from '@shared/models/worked-shift';

import { ShiftValidationService } from '../../services/shift-validation/shift-validation.service';
import { ShiftContextService } from '../../services/shift-context.service';
import { LoadsSummary } from '../../models/shift-validation/loads-summary';
import { ShiftContext } from '../../models/shift-context';

@Component({
    selector: 'app-complete-shift',
    templateUrl: './complete-shift.component.html',
    styleUrls: ['./complete-shift.component.scss']
})
export class CompleteShiftComponent implements OnInit, OnDestroy {
    ShiftStatus = ShiftStatus;

    items = new BehaviorSubject<MenuItem[]>([]);
    activeItemIndex = new BehaviorSubject<number>(0);
    activeItem = combineLatest([ this.activeItemIndex, this.items ])
        .pipe(map(([i, items]) => i >= items?.length ? null : items[i]));
    isLastPage = combineLatest([ this.activeItemIndex, this.items ])
    .pipe(map(([i, items]) => i === items?.length - 1));

    loadsSummary: LoadsSummary;
    subscriptions: Subscription[];
    startupSubscription: Subscription;
    displayCompleteShiftModal: boolean;
    closingShift: boolean = false;
    context = new BehaviorSubject<ShiftContext | null>(null);
    showDialog: boolean = false;
    subinventories: SiteSubinventory[];

    nextPreloaded: boolean = false;

    childIsValid = this.shiftValidationService.IsValid;

    @ViewChild('steps', { static: true }) steps: Steps;

    constructor(
        private shiftValidationService: ShiftValidationService,
        private workedShiftService: WorkedShiftsService,
        private activityService: ActivityBlockingService,
        private shiftContextService: ShiftContextService,
        private messageService: MessageService,
        private router: Router) { }

    ngOnInit(): void {
        this.subscriptions = [
            this.shiftContextService.CurrentContext
                .pipe(
                    distinctUntilChanged<ShiftContext>((a, b) => a?.id === b?.id),
                    switchMap(context => {
                        this.context.next(context);
                        return this.shiftValidationService.startCalculations(context.id);
                    }),
                    switchMap(_ => this.shiftContextService.SiteConfig),
                    this.activityService.Wait('Complete Shift'))
                .subscribe(configs => {
                    this.subinventories = this.context.value.site.subinventories
                        .filter(s => s.isActive && s.subinventoryType !== SubinventoryType.UsedAssetCenter)
                        .sort((a, b) => a.subinventoryType - b.subinventoryType);

                    const items = [
                        { label: 'Loads Summary', routerLink: 'loads-summary', title: 'Loads' },
                        ...this.subinventories
                            .filter(s => !! s.name)
                            .map(s => this.setMenuItem(s)),
                        { label: 'End of Shift Files', routerLink: 'end-of-shift-files'},
                        ...this.subinventories.some(s => s.subinventoryType === SubinventoryType.ChepPlantPallets)
                            ? [{ label: 'CHEP Activity', routerLink: 'chep-activity' }]
                            : []
                    ];

                    const radConfig = configs.find(c => c.configKey === SiteConfig.SITE_RAD_TRANS_ENABLE)?.GetValue<boolean>() ?? false;
                    const vopConfig = configs.find(c => c.configKey === SiteConfig.SITE_VOP_TRANS_ENABLE)?.GetValue<boolean>() ?? false;
                    const pecoConfig = configs.find(c => c.configKey === SiteConfig.SITE_PECO_TRANS_ENABLE)?.GetValue<boolean>() ?? false;
                    const rm2Config = configs.find(c => c.configKey === SiteConfig.SITE_RM2_TRANS_ENABLE)?.GetValue<boolean>() ?? false;

                    [SiteConfig.SITE_RAD_TRANS_ENABLE, SiteConfig.SITE_VOP_TRANS_ENABLE, SiteConfig.SITE_PECO_TRANS_ENABLE, SiteConfig.SITE_RM2_TRANS_ENABLE]

                    if (radConfig || vopConfig || pecoConfig || rm2Config) {
                        const menuitem = { label: 'Internal Transfers', routerLink: 'internal-transfers', title: 'InternalTransfers' };
                        const index = this.subinventories
                            .map((subinventory, index) => ({ subinventory, index }))
                            .filter(p => p.subinventory.subinventoryType === SubinventoryType.ChepPlantPallets
                                || p.subinventory.subinventoryType === SubinventoryType.TpmProduction
                                || p.subinventory.subinventoryType === SubinventoryType.VendorOnPremise
                                || p.subinventory.subinventoryType === SubinventoryType.PecoPallets
                                || p.subinventory.subinventoryType === SubinventoryType.Rm2Pallets)
                            .at(-1)
                            .index + 1; // +1 for load summary

                        if (index === items.length - 1)
                            items.push(menuitem);
                        else {
                            items.splice(index + 1 , 0, menuitem);
                        }
                    }

                    this.items.next(items);
                    this.activeItemIndex.next(0);
                }),

            combineLatest([ this.activeItem, this.context ])
                .pipe(
                    filter(([item, context]) => !!item && !!context),
                    distinctUntilChanged((a, b) => a[0].routerLink === b[0].routerLink && a[1].id === b[1].id))
                .subscribe(([item, context]) => {
                    this.router.navigate([`complete-shift/${item.routerLink}`]);

                    if (this.items.value.length > this.activeItemIndex.value + 1) {
                        this.nextPreloaded = false;

                        const next = this.items.value[this.activeItemIndex.value + 1];

                        if (next.title) {
                            this.shiftValidationService.preloadSummary(context.id, next.title)
                                .subscribe({
                                    next: _ => {
                                        this.nextPreloaded = true;
                                    },
                                    error: _ => {}
                                });
                        }
                        else {
                            this.nextPreloaded = true;
                        }
                    }
                }),
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.shiftValidationService.reset();
    }

    setMenuItem(subInventory: SiteSubinventory): MenuItem {
        let localRoute = '';
        let apiRoute = '';

        switch (subInventory.subinventoryType) {
            case SubinventoryType.ChepComponents:
            case SubinventoryType.PecoComponents:
                apiRoute = url`Components/${subInventory.id}`;
                localRoute = url`components-summary/${subInventory.id}`;
                break;
            case SubinventoryType.WhiteWoodComponents:
                apiRoute = url`WhiteWoodComponents/${subInventory.id}`;
                localRoute = url`ww-components/${subInventory.id}`;
                break;
            default:
                apiRoute = url`Summaries/${subInventory.id}`;
                localRoute = url`summary/${subInventory.id}`;
        }

        return {
            label: subInventory.name,
            title: apiRoute,
            routerLink: localRoute
        };
    }

    nextPage(): void {
        if (this.items.value.length) {
            const index = this.activeItemIndex.value + 1;

            if (index === this.items.value.length) {
                this.showCompleteShiftPopup();
            }
            else {
                this.activeItemIndex.next(index);
            }
        }
    }

    previousPage(): void {
        if (this.items.value.length) {
            const index = this.activeItemIndex.value - 1;

            if (index < 0)
                return;

            this.activeItemIndex.next(index);
        }
    }

    showCompleteShiftPopup(): void {
        this.displayCompleteShiftModal = true;
    }

    closeCompleteShiftPopup(): void {
        this.displayCompleteShiftModal = false;
    }

    closeShift(): void {
        this.closingShift = true;
        const context = this.context.value;
        this.workedShiftService.SetWorkedShiftStatus(context.id, ShiftStatus.Closed).subscribe({
            next: _ws => {
                this.closeCompleteShiftPopup();

                this.messageService.add({
                    severity: 'success',
                    summary: 'Shift Closed',
                    detail: `${context.date.toLocaleString()} shift ${context.shift.number} has been closed.`
                });

                this.router.navigateByUrl('/');
                this.closingShift = false;
            },
            error: _err => {
                this.closingShift = false;
            }
        });
    }
}
